import SummarizeIcon from '@mui/icons-material/Summarize'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import SearchIcon from '@mui/icons-material/Search'
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar'
import WifiIcon from '@mui/icons-material/Wifi'
import Groups3Icon from '@mui/icons-material/Groups3'
import InsightsIcon from '@mui/icons-material/Insights'
import WorkHistoryIcon from '@mui/icons-material/WorkHistory'
import { Autocomplete, Box, Container, InputAdornment, Paper, TextField, Typography } from '@mui/material'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import ptBR from 'date-fns/locale/pt-BR'
import { format } from 'date-fns'

import useProtectedPage from 'src/hooks/useProtectedPage'
import { ROUTES } from 'src/routes/Router'
import { getDashboardReport, getVisitorsReport } from 'src/service/dashboardService'
import { AlertComponent } from 'src/components/Alert/AlertComponent'
import { AlertContext } from 'src/context/AlertContext'
import { useGlobalSelectedCompany } from 'src/context/GlobalSelectedCompanyContext'
import { getPermissions, getRole } from 'src/service/authService'
import { ReportGeneratorService } from './helpers/reports-generator-service'
import { ReportAccordionItem } from './components/accordion-item'
import { getAccessPointsAnalysis, getProfileDataByAccessPoint } from 'src/service/accesspointService'
import { getSystemAccessPoints, getSystemGroups } from 'src/service/marketingCampaignService'
import { getProfileDataByGroup } from 'src/service/pointGroupsService'
import { ReportConfig, ReportFilters } from './helpers/reports-page-types'
import { reportFormats } from './helpers/reports-page-constants'

export const ViewReports = () => {
  useProtectedPage()
  const reportService = new ReportGeneratorService()
  const { t, i18n } = useTranslation()
  const { createAlert } = useContext(AlertContext)
  const { isGlobalAdminSelected, selectedCompanyData } = useGlobalSelectedCompany()
  const isTecnoit = getRole() === 'TECNOIT'
  const permissions = JSON.parse(getPermissions())

  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [reportDataFilter, setReportDataFilter] = useState<ReportFilters>({
    startDate: null,
    endDate: null,
    format: null,
  })

  const handleToggle = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }, [])

  const reports: ReportConfig[] = useMemo(() => {
    const companyId = isGlobalAdminSelected ? undefined : selectedCompanyData?.id;

    return [
      {
        id: 'reports:visitors',
        name: t('reports.page.report.visitors.name'),
        description: t('reports.page.report.visitors.description'),
        icon: <PermContactCalendarIcon />,
        filters: [],
        onDownload: async (dataFilters: ReportFilters) => {
          const selectedFormat = dataFilters?.format?.value ?? '.csv'
          createAlert(
            ROUTES.REPORTS,
            `${t('dashboard.exportation.of')} ${t(`dashboard.export.userData`)} (${dataFilters?.format?.value.toUpperCase()}) ${t('dashboard.exportation.running')}`,
            'warning'
          )
          const reportData = await getVisitorsReport({
            params: {
              time: '7d',
              dateStart: format(dataFilters?.startDate, 'dd/MM/yyyy'),
              dateEnd: format(dataFilters?.endDate, 'dd/MM/yyyy'),
            },
          })
          const { headers, data } = reportData
          reportService.generateAndDownloadFile(data, 'itBuzz-consent-data', selectedFormat, headers)
          createAlert(
            ROUTES.REPORTS,
            `${t('dashboard.exportation.of')} ${t(`dashboard.export.userData`)} (${dataFilters?.format?.value.toUpperCase()}) ${t('dashboard.exportation.done')}`
          )
        }
      },
      {
        id: 'reports:ap-profile',
        name: t('reports.page.report.apProfile.name'),
        description: t('reports.page.report.apProfile.description'),
        icon: <WifiIcon />,
        filters: [
          {
            name: 'accessPointId',
            label: t('reports.page.report.apProfile.filter.ap'),
            type: 'autocomplete' as const,
            required: true,
            placeholder: 'Busque pelo nome...',
            fetchKey: isGlobalAdminSelected ? 'global' : `company-${companyId}`,
            fetchOptions: async () => {
              const options = isTecnoit ? { companyId: selectedCompanyData.id } : {}
              const data = await getSystemAccessPoints({
                params: { size: 100000000, ...options },
              })
              if (!data?.listAccessPoints) {
                createAlert('/', t('form.pointgroup.notify.no.points'))
              }
              return data.listAccessPoints.map((ap) => ({
                label: ap.apName,
                value: ap.id,
              }));
            },
          },
        ],
        onDownload: async (dataFilters: ReportFilters, filters: Record<string, unknown>) => {
          const selectedFormat = dataFilters?.format?.value ?? '.csv'
          createAlert(
            ROUTES.REPORTS,
            `${t('dashboard.exportation.of')} ${t(`dashboard.export.apProfile`)} (${dataFilters?.format?.value.toUpperCase()}) ${t('dashboard.exportation.running')}`,
            'warning'
          )
          const reportData = await getProfileDataByAccessPoint(filters.accessPointId as string, { params: isTecnoit ? { companyId: selectedCompanyData.id } : {} })
          if (reportData) {
            const headers = ['question', 'total', 'answers']
            const formattedData = reportData.map((item) => {
              const { answers, ...rest } = item
              // Concatenando respostas em uma única string com ponto e vírgula
              const answersConcat = Object.keys(answers).map(key => `${key}: ${answers[key]}`).join('; ')
                return {
                  ...rest,
                  answers: answersConcat,
                }
            })
            reportService.generateAndDownloadFile(formattedData, 'itBuzz-access-point-profile', selectedFormat, headers, true)
          }
          createAlert(
            ROUTES.REPORTS,
            `${t('dashboard.exportation.of')} ${t(`dashboard.export.apProfile`)} (${dataFilters?.format?.value.toUpperCase()}) ${t('dashboard.exportation.done')}`
          )
        }
      },
      {
        id: 'reports:group-profile',
        name: t('reports.page.report.groupProfile.name'),
        description: t('reports.page.report.groupProfile.description'),
        icon: <Groups3Icon />,
        filters: [
          {
            name: 'groupId',
            label: t('reports.page.report.apProfile.filter.group'),
            type: 'autocomplete' as const,
            required: true,
            placeholder: 'Busque pelo nome...',
            fetchKey: isGlobalAdminSelected ? 'global' : `company-${companyId}`,
            fetchOptions: async () => {
              const options = isTecnoit ? { companyId: selectedCompanyData.id } : {}
              const data = await getSystemGroups({
                params: { size: 100000000, ...options },
              })
              if (!data?.listGroups) {
                createAlert('/', t('form.pointgroup.notify.no.points'))
              }
              return data.listGroups.map((group) => ({
                label: group.groupName,
                value: group.id,
              }));
            },
          },
        ],
        onDownload: async (dataFilters: ReportFilters, filters: Record<string, unknown>) => {
          const selectedFormat = dataFilters?.format?.value ?? '.csv'
          createAlert(
            ROUTES.REPORTS,
            `${t('dashboard.exportation.of')} ${t(`dashboard.export.groupProfile`)} (${dataFilters?.format?.value.toUpperCase()}) ${t('dashboard.exportation.running')}`,
            'warning'
          )
          const reportData = await getProfileDataByGroup(filters.groupId as string, { params: isTecnoit ? { companyId: selectedCompanyData.id } : {} })
          if (reportData) {
            const headers = ['question', 'total', 'answers']
            const formattedData = reportData.map((item) => {
              const { answers, ...rest } = item
              // Concatenando respostas em uma única string com ponto e vírgula
              const answersConcat = Object.keys(answers).map(key => `${key}: ${answers[key]}`).join('; ')
              return {
                ...rest,
                answers: answersConcat,
              }
            })
            reportService.generateAndDownloadFile(formattedData, 'itBuzz-group-profile', selectedFormat, headers, true)
          }
          createAlert(
            ROUTES.REPORTS,
            `${t('dashboard.exportation.of')} ${t(`dashboard.export.groupProfile`)} (${dataFilters?.format?.value.toUpperCase()}) ${t('dashboard.exportation.done')}`
          )
        }
      },
      {
        id: 'reports:dashboard',
        name: t('reports.page.report.dashboard.name'),
        description: t('reports.page.report.dashboard.description'),
        icon: <WorkHistoryIcon />,
        filters: [],
        onDownload: async (dataFilters: ReportFilters) => {
          const selectedFormat = dataFilters?.format?.value ?? '.csv'
          createAlert(
            ROUTES.REPORTS,
            `${t('dashboard.exportation.of')} ${t(`dashboard.export.userLogin`)} (${dataFilters?.format?.value.toUpperCase()}) ${t('dashboard.exportation.running')}`,
            'warning'
          )
          const reportData = await getDashboardReport({
            params: {
              time: '7d',
              dateStart: format(dataFilters?.startDate, 'dd/MM/yyyy'),
              dateEnd: format(dataFilters?.endDate, 'dd/MM/yyyy'),
            },
          })
          const formattedOutput = reportData.map((item) => {
            const date = new Date(item.login_date)
            return {
              ...item,
              login_date: date.toLocaleString('pt-BR', {
                timeZone: 'America/Sao_Paulo',
                hour12: false,
              }),
            }
          })
          const headers = ['login_date', 'useragent', 'user_mac_address', 'groups']
          reportService.generateAndDownloadFile(formattedOutput, 'itBuzz-access-report', selectedFormat, headers)
          createAlert(
            ROUTES.REPORTS,
            `${t('dashboard.exportation.of')} ${t(`dashboard.export.userLogin`)} (${selectedFormat.toUpperCase()}) ${t('dashboard.exportation.running')}`,
          )
        }
      },
      {
        id: 'reports:ap-analysis',
        name: t('reports.page.report.apAnalysis.name'),
        description: t('reports.page.report.apAnalysis.description'),
        icon: <InsightsIcon />,
        filters: [],
        onDownload: async (dataFilters: ReportFilters) => {
          const selectedFormat = dataFilters?.format?.value ?? '.csv'
          if (isGlobalAdminSelected() && isTecnoit) {
            createAlert('/', t('point.report.data.error'), 'error')
            return
          } else {
            createAlert(
              ROUTES.REPORTS,
              `${t('dashboard.exportation.of')} ${t(`dashboard.export.apAnalysis`)} (${dataFilters?.format?.value.toUpperCase()}) ${t('dashboard.exportation.running')}`,
              'warning'
            )
            const response = await getAccessPointsAnalysis({ params: isTecnoit ? { companyId: selectedCompanyData.id } : {} })
            reportService.generateAndDownloadFile(
              response.accessPoints,
              `itBuzz_access_points_analysis_${new Date().toLocaleDateString()}`,
              selectedFormat,
              ['id', 'apName', 'geoAddress', 'geoLatitude', 'geoLongitude', 'lastConnection', 'totalConnections']
            )
            createAlert(
              ROUTES.REPORTS,
              `${t('dashboard.exportation.of')} ${t(`dashboard.export.apAnalysis`)} (${selectedFormat.toUpperCase()}) ${t('dashboard.exportation.running')}`,
            )
          }
        }
      },
    ]
  }, [isGlobalAdminSelected, selectedCompanyData?.id, i18n.language])

  const availableReportsForUser = reports.filter((r) =>
    permissions?.reports?.includes(r.id)
  )

  const filteredReports = availableReportsForUser.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <Container
      sx={{
        mt: '96px',
      }}
    >
      <AlertComponent />
      <Box
        component="div"
        sx={{
          color: '#FF5E1E',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '24px',
        }}
      >
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <SummarizeIcon />
          <Typography
            variant="h6"
            sx={{
              fontWeight: '500',
              fontSize: '20px',
              lineHeight: '160%',
              letterSpacing: ' 0.15px',
              color: ' #FF5E1E',
              marginTop: '-4px',
            }}
          >
            {t('reports.page.title')}
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <CalendarTodayIcon color="action" />
          <Typography variant="subtitle2" color="text.secondary">
            {t('reports.page.period.selection')}
          </Typography>

          <DatePicker
            label={t('reports.page.date.start.selection')}
            value={reportDataFilter.startDate}
            onChange={(date) =>
              setReportDataFilter((prev) => ({ ...prev, startDate: date }))
            }
            maxDate={reportDataFilter.endDate ?? undefined}
            renderInput={(params) => (
              <TextField {...params} size="small" sx={{ width: 180 }} />
            )}
          />

          <DatePicker
            label={t('reports.page.date.end.selection')}
            value={reportDataFilter.endDate}
            onChange={(date) =>
              setReportDataFilter((prev) => ({ ...prev, endDate: date }))
            }
            minDate={reportDataFilter.startDate ?? undefined}
            renderInput={(params) => (
              <TextField {...params} size="small" sx={{ width: 180 }} />
            )}
          />

          <Typography variant="subtitle2" color="text.secondary" sx={{ ml: 2 }}>
            {t('reports.page.format.selection')}
          </Typography>

          <Autocomplete
            options={reportFormats}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            value={reportDataFilter.format}
            onChange={(_, newVal) =>
              setReportDataFilter((prev) => ({ ...prev, format: newVal }))
            }
            size="small"
            sx={{ width: 210 }}
            renderInput={(params) => (
              <TextField {...params} label={t('reports.page.format.label')} placeholder={t('reports.page.format.placeholder')} />
            )}
          />

          <TextField
            placeholder={t('reports.page.search.placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ ml: 'auto', width: { xs: '100%', sm: 220 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </LocalizationProvider>
      </Paper>
      <Box
        sx={{
          mb: '40px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '100%',
        }}
      >
        {filteredReports.length === 0 ? (
          <Typography color="text.primary" sx={{ textAlign: 'center', py: 6 }}>
            {t('reports.page.empty.list')}
          </Typography>
        ) : (
          filteredReports.map((report) => (
            <ReportAccordionItem
              key={report.id}
              report={report}
              dateFilter={reportDataFilter}
              expanded={expandedId === report.id}
              onToggle={handleToggle}
            />
          ))
        )}
      </Box>
    </Container>
  )
}
